/**
 * 「计时到点了」提示音的**判据**（纯函数：无 React、无 DOM、无音频、无网络）。
 *
 * ## 这件事要解决什么
 *
 * 孩子提的需求：「计时达到预计时间时，能不能发出提示音？」
 * 计时是服务端权威的（见 `timer-clock.ts` 与 `useServerTimer`），
 * 所以"到点了没有"这件事本身只是一个比较：
 *
 * > 已用秒数 >= 这一项的预计秒数
 *
 * 难的不是这个比较，而是**什么时候该响、什么时候不该响**：
 *
 *  1. **只响一次**。已用秒数每秒都在变，若"满足条件就响"，就变成每秒一次警报。
 *     所以要过阈值那**一下**，不是"过了阈值就一直响"。
 *  2. **暂停时不响**。暂停不是"时间到了"，只是孩子按下去了。
 *  3. **恢复后不重复响**。恢复那一刻，已用秒数**本来就已经**超过预计时长了
 *     （暂停前响过），若判据是"当前值 >= 阈值"，恢复瞬间必然又响一声 ——
 *     这正是要避免的"响第二次"。所以"响过没有"必须跟着**这一次计时会话**记账。
 *  4. **没有预计时长就不响**（`null` / `0` / 负数 / NaN）—— 没有阈值，
 *     没有"到点"可言。⛔ 不能把它当成 0 秒（那会在开始那一刻就响）。
 *  5. **刷新页面不该响**。孩子计时到 25 分钟（预计 20 分钟）时刷新一下，
 *     页面重新进来时"已用 25 分钟"是既成事实，不是"刚刚跨过阈值" ——
 *     每刷新一次"叮"一声才是真的打扰。所以**开局第一眼就已经在阈值之上 =
 *     只记账、不响**。
 *
 * ## 为什么抽成纯函数
 *
 * 与 `timer-clock.ts` 同一条理由：项目里可测的判据不要埋在组件里。
 * 下面这一串"什么时候该响"因此可以逐条断言（见
 * `__tests__/estimate-chime.test.ts`），组件只需要把界面上的两个数喂进来、
 * 在 `shouldChime` 为真时放一声。
 *
 * ## 与界面同源
 *
 * 阈值用的就是界面上「预计 20 分钟」那一份 `estimatedSeconds`
 * （`StudentTodayPage` / `StudentHomeworkPage` 显示的就是它），
 * 已用秒数用的就是界面上大号数字那一份 `elapsedSeconds`。
 * **不另算一套**：界面上写着「预计 20 分钟」，提示音就必须在 20 分钟那一刻响。
 */

/**
 * 状态机的状态。
 *
 * ⚠️ 它是**跟着一次计时会话**记的：`sessionId` 是这一次计时的打卡 id。
 * 换了一条打卡/作业项 = 新的一次计时会话 = 可以重新响一次。
 */
export interface EstimateChimeState {
  /** 这份状态属于哪一次计时会话（`null` = 还没见过会话）。 */
  sessionId: string | null;
  /** 本次会话是否已经响过（含"开局就已经超过预计时长"这种**静默记账**）。 */
  chimed: boolean;
}

/**
 * 初始状态：没有会话、没响过。
 *
 * 与 `initialTimerAnchor()` 一样做成函数而不是共享常量对象 ——
 * 每个调用点（每一条打卡行）都拿到自己的那一份。
 */
export function initialEstimateChimeState(): EstimateChimeState {
  return { sessionId: null, chimed: false };
}

/** 一次观测：调用方（React 组件）把界面上那几个数原样喂进来。 */
export interface EstimateChimeObservation {
  /** 正在计时的打卡 id；`null` = 没有在计时的会话（没开始 / 暂停 / 已结束）。 */
  sessionId: string | null;
  /** 是否正在计时。暂停、终态一律 `false`。 */
  isRunning: boolean;
  /** 界面上显示的那个已用秒数（服务端锚点 + 本地单调推进）。 */
  elapsedSeconds: number;
  /**
   * 服务端已确认的已用秒数（`actualSeconds`），与 `useServerTimer` 里的
   * `serverSeconds` 同名同义。
   *
   * 为什么需要它：组件**第一帧**拿到的 `elapsedSeconds` 还是 0
   * （锚点要等 effect 跑完才生效），若只看它，刷新页面时就会看到
   * "0 → 1500"这一跳，被误判成"刚刚跨过阈值"从而响一声。
   */
  serverSeconds: number;
  /** 这一项的预计时长（秒）；`null` = 没设过 → 永远不响。 */
  estimatedSeconds: number | null | undefined;
}

/** 状态机的一次推进结果。 */
export interface EstimateChimeDecision {
  /** 推进后的状态，调用方要存下来喂下一次。 */
  state: EstimateChimeState;
  /** 这一次要不要放提示音。 */
  shouldChime: boolean;
}

/** 合法的秒数（负数 / NaN / Infinity → 0），与 `timer-clock.ts` 同一口径。 */
function toSeconds(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

/**
 * 预计时长 → 阈值。`null` = **没有阈值**（不响）。
 *
 * ⚠️ `0` / 负数 / NaN 一并当成"没有阈值"：把 0 当阈值的话，
 * 开始那一刻（已用 0 秒）就满足 `elapsed >= 0`，孩子一点「开始」就是一声。
 */
function thresholdOf(estimatedSeconds: number | null | undefined): number | null {
  if (typeof estimatedSeconds !== 'number' || !Number.isFinite(estimatedSeconds) || estimatedSeconds <= 0) {
    return null;
  }
  return estimatedSeconds;
}

/**
 * 推进一次"到点提示音"状态机。
 *
 * 判定顺序（每一条都对应上面文件头里的一条要求）：
 *
 *  1. 没有正在计时的会话（`sessionId` 为空或不在计时）→ **原样返回，不响**。
 *     刻意**保留** `previous`（含"已经响过"）：暂停/恢复不能把记账清掉，
 *     否则恢复瞬间会因为"已经超过预计时长"再响一次。
 *  2. `sessionId` 变了 = 新的一次计时会话 → 重建状态，**这一拍不响**：
 *     若开局就已经在阈值之上（刷新页面 / 切回来），只静默记账；
 *     否则记成"还没响"，等它真的跨过阈值。
 *  3. 已经响过 → 不响（这条就是"只响一次"）。
 *  4. 没有预计时长 → 不响。
 *  5. 还没到阈值 → 不响，等下一拍。
 *  6. 到阈值了 → 响，并记账。
 *
 * `elapsedSeconds` 与 `serverSeconds` 取**较大者**参与比较（见
 * `serverSeconds` 字段的说明：组件第一帧的 `elapsedSeconds` 还是 0）。
 */
export function advanceEstimateChime(
  previous: EstimateChimeState,
  observation: EstimateChimeObservation,
): EstimateChimeDecision {
  const { sessionId, isRunning, estimatedSeconds } = observation;

  // 1. 没有正在计时的会话：不响，也不丢记账
  if (!sessionId || !isRunning) {
    return { state: previous, shouldChime: false };
  }

  const knownSeconds = Math.max(toSeconds(observation.elapsedSeconds), toSeconds(observation.serverSeconds));
  const threshold = thresholdOf(estimatedSeconds);

  // 2. 换了一次计时会话
  if (sessionId !== previous.sessionId) {
    return {
      state: { sessionId, chimed: threshold !== null && knownSeconds >= threshold },
      shouldChime: false,
    };
  }

  // 3. 只响一次
  if (previous.chimed) return { state: previous, shouldChime: false };

  // 4. 没有预计时长就没有"到点"
  if (threshold === null) return { state: previous, shouldChime: false };

  // 5. 还没到
  if (knownSeconds < threshold) return { state: previous, shouldChime: false };

  // 6. 跨过阈值的那一刻：响，并且**只响这一次**
  return { state: { sessionId, chimed: true }, shouldChime: true };
}
